import {
  Stack,
  Typography,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
} from '@mui/material';
import { Card } from '@liminal/web-ui/primitives/Card';
import { useUserPortfolio } from '@/hooks/useUsers';
import { formatCurrencyAmount } from '@liminal/utils/currency';
import { MoneyAmount } from '@liminal/web-ui/display/MoneyAmount';
import type { MoneyDirection } from '@liminal/web-ui/display/MoneyAmount';
import { StatusPill } from '@liminal/web-ui/primitives/StatusPill';
import { DataTableBody } from '@liminal/web-ui/display/DataTableBody';
import { StatGrid } from '@liminal/web-ui/display/StatGrid';
import { WarningCard } from '@liminal/web-ui/feedback/WarningCard';
import { MonoCell } from '@/components/ui/MonoCell';
import { SortableTableCell } from '@liminal/web-ui/display/SortableTableCell';
import { useTableSort } from '@liminal/web-ui/primitives/useTableSort';
import type { UserPosition } from '@/api/types';

/**
 * A figure at or above zero is a gain, below it a loss.
 *
 * The API sends these as decimal strings and they stay strings: the sign is
 * read off the text, as useTableSort orders them, so no figure goes through a
 * float on the way to the screen (CLAUDE.md, money never as a float). A
 * `-0.00` reads as a loss, which is what the backend meant by writing the sign.
 */
const isGain = (value?: string): boolean => !(value ?? '0').trim().startsWith('-');

const pnlDirection = (value?: string): MoneyDirection => (isGain(value) ? 'gain' : 'loss');

/** Prefixes a gain with `+`. Losses already carry their own minus sign. */
const signed = (value: string | undefined, formatted: string) =>
  isGain(value) ? `+${formatted}` : formatted;

/**
 * The whole portfolio arrives in one response, so this is a true sort over
 * every position rather than over a page of them.
 *
 * Every figure is handed over as the decimal string the API sent: the
 * comparator orders decimals exactly, where a `parseFloat` here would put
 * money through a float on its way to a `<`.
 */
const SORT_ACCESSORS = {
  assetId: (pos: UserPosition) => pos.assetId,
  symbol: (pos: UserPosition) => pos.symbol,
  name: (pos: UserPosition) => pos.name,
  quantity: (pos: UserPosition) => pos.quantity,
  markPrice: (pos: UserPosition) => pos.markPrice,
  marketValue: (pos: UserPosition) => pos.marketValue,
  avgCostPerShare: (pos: UserPosition) => pos.avgCostPerShare,
  costBasis: (pos: UserPosition) => pos.costBasis,
  unrealizedPnl: (pos: UserPosition) => pos.unrealizedPnl,
  realizedPnl: (pos: UserPosition) => pos.realizedPnl,
  totalPnl: (pos: UserPosition) => pos.totalPnl,
  pnlPercent: (pos: UserPosition) => pos.pnlPercent,
};

// One array drives the header cells and the DataTableBody column count.
const COLUMNS = [
  { column: 'assetId', label: 'Asset ID' },
  { column: 'symbol', label: 'Symbol' },
  { column: 'name', label: 'Name' },
  { column: 'quantity', label: 'Quantity' },
  { column: 'markPrice', label: 'Mark Price' },
  { column: 'marketValue', label: 'Market Value' },
  { column: 'avgCostPerShare', label: 'Avg Cost/Share' },
  { column: 'costBasis', label: 'Cost Basis' },
  { column: 'unrealizedPnl', label: 'Unrealized PnL' },
  { column: 'realizedPnl', label: 'Realized PnL' },
  { column: 'totalPnl', label: 'Total PnL' },
  { column: 'pnlPercent', label: 'PnL %' },
] as const;

// -- Styled components --

export function InvestmentsSection({ userId }: { userId: string }) {
  const { data, isLoading, isError } = useUserPortfolio(userId);

  // Left on the response's own order until a header is clicked, since the
  // API does not say what it is.
  const { rows: positions, sort } = useTableSort(data?.positions ?? [], SORT_ACCESSORS);

  return (
    <Stack spacing={3}>
      {/* A failed request says so. It used to read "No investment data
          available for this user", which is what an account with no holdings
          says. */}
      {isError && (
        <WarningCard tone="error">Could not load this user's portfolio.</WarningCard>
      )}

      {/* Portfolio Summary. P&L is money, so it takes a gain/loss direction
          rather than the success/error palette: those render the same green and
          red today only because state.success and semantic.gain share a hex;
          they were different colours until LIM-2474. See tokens/semantic.ts. */}
      {(isLoading || data) && (
        <Stack spacing={1}>
          <Typography variant="h6">Portfolio Summary</Typography>
          <StatGrid
            columns={3}
            stats={[
              {
                id: 'total-value',
                label: 'Total Value (USD)',
                value: formatCurrencyAmount(data?.totalValueUsd, 'USD'),
                loading: isLoading,
              },
              {
                id: 'total-pnl',
                label: 'Total PnL (USD)',
                value: signed(data?.totalPnlUsd, formatCurrencyAmount(data?.totalPnlUsd, 'USD')),
                direction: pnlDirection(data?.totalPnlUsd),
                loading: isLoading,
              },
              {
                id: 'total-pnl-percent',
                label: 'Total PnL %',
                value: signed(data?.totalPnlPercent, `${data?.totalPnlPercent ?? '0'}%`),
                direction: pnlDirection(data?.totalPnlPercent),
                loading: isLoading,
              },
            ]}
          />
        </Stack>
      )}

      {/* Positions Table */}
      <Card variant="elevated" padding="none">
          <Typography variant="h6" sx={{ px: 2, pt: 2, pb: 1 }}>
            Positions
          </Typography>
          <TableContainer>
            <Table sx={{ minWidth: 1400 }}>
              <TableHead>
                <TableRow>
                  {COLUMNS.map(({ column, label }) => (
                    <SortableTableCell key={column} column={column} sort={sort}>
                      {label}
                    </SortableTableCell>
                  ))}
                </TableRow>
              </TableHead>
              <DataTableBody
                columns={COLUMNS.length}
                isLoading={isLoading}
                isError={isError}
                isEmpty={positions.length === 0}
                emptyMessage="No investment positions found."
                errorMessage="Could not load this user's positions."
              >
                {positions.map((pos, i) => {
                  const pnlPercentUp = isGain(pos.pnlPercent);
                  const totalPnlUp = isGain(pos.totalPnl);

                  return (
                    <TableRow key={pos.assetId ?? i}>
                      <MonoCell>{pos.assetId ?? '—'}</MonoCell>
                      <TableCell>
                        <Typography fontWeight={600}>{pos.symbol ?? '—'}</Typography>
                      </TableCell>
                      <TableCell>{pos.name ?? '—'}</TableCell>
                      <TableCell>{pos.quantity ?? '0'}</TableCell>
                      <TableCell>{formatCurrencyAmount(pos.markPrice, 'USD')}</TableCell>
                      <TableCell>{formatCurrencyAmount(pos.marketValue, 'USD')}</TableCell>
                      <TableCell>{formatCurrencyAmount(pos.avgCostPerShare, 'USD')}</TableCell>
                      <TableCell>{formatCurrencyAmount(pos.costBasis, 'USD')}</TableCell>
                      <TableCell>
                        <MoneyAmount direction={pnlDirection(pos.unrealizedPnl)}>
                          {formatCurrencyAmount(pos.unrealizedPnl, 'USD')}
                        </MoneyAmount>
                      </TableCell>
                      <TableCell>
                        <MoneyAmount direction={pnlDirection(pos.realizedPnl)}>
                          {formatCurrencyAmount(pos.realizedPnl, 'USD')}
                        </MoneyAmount>
                      </TableCell>
                      <TableCell>
                        <StatusPill
                          label={`${totalPnlUp ? '+' : ''}${formatCurrencyAmount(pos.totalPnl, 'USD')}`}
                          tone={totalPnlUp ? 'gain' : 'loss'}
                          size="sm"
                        />
                      </TableCell>
                      <TableCell>
                        <StatusPill
                          label={`${pnlPercentUp ? '+' : ''}${pos.pnlPercent ?? '0'}%`}
                          tone={pnlPercentUp ? 'gain' : 'loss'}
                          size="sm"
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </DataTableBody>
            </Table>
          </TableContainer>
      </Card>
    </Stack>
  );
}
